import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useToast } from '@/hooks/useToast';
import {
  getApprovalSettings,
  saveApprovalSettings,
  resetApprovalSettings,
  addApprovalLevel,
  removeApprovalLevel,
  reorderApprovalLevels,
  type ApprovalSettings,
  type ApprovalLevel,
  type ApprovalDepartmentScope,
  ROLE_OPTIONS,
  DEPARTMENT_OPTIONS,
} from '@/lib/settings';
import {
  RotateCcw,
  Save,
  Info,
  Plus,
  Trash2,
  AlertCircle,
  Users,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';

export default function SettingsApprovalTab() {
  const [settings, setSettings] = useState<ApprovalSettings>(getApprovalSettings);
  const [saved, setSaved] = useState(false);
  const [levelToDelete, setLevelToDelete] = useState<ApprovalLevel | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const { success: toastSuccess } = useToast();

  // New level form state
  const [newLevel, setNewLevel] = useState<Omit<ApprovalLevel, 'id' | 'level'>>({
    name: '',
    approverType: 'role',
    approverRole: 'manager',
    approverUserId: '',
    departmentScope: ['all'],
    conditionType: 'always',
    amountThreshold: 0,
  });

  useEffect(() => {
    setSettings(getApprovalSettings());
  }, []);

  const handleSave = () => {
    saveApprovalSettings(settings);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
    toastSuccess('Settings saved', 'Approval settings have been updated.');
  };

  const handleReset = () => {
    setSettings(resetApprovalSettings());
    toastSuccess('Settings reset', 'Approval settings have been reset to defaults.');
  };

  const handleAddLevel = () => {
    if (!newLevel.name.trim()) {
      toastSuccess('Error', 'Please enter a level name.');
      return;
    }
    
    const updated = addApprovalLevel(newLevel);
    setSettings(updated);
    setShowAddForm(false);
    setNewLevel({
      name: '',
      approverType: 'role',
      approverRole: 'manager',
      approverUserId: '',
      departmentScope: ['all'],
      conditionType: 'always',
      amountThreshold: 0,
    });
    toastSuccess('Level added', `Approval level "${newLevel.name}" has been added.`);
  };

  const handleDeleteLevel = (level: ApprovalLevel) => {
    const updated = removeApprovalLevel(level.id);
    setSettings(updated);
    setLevelToDelete(null);
    toastSuccess('Level removed', `Approval level "${level.name}" has been removed.`);
  };

  const handleMoveLevel = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= settings.levels.length) return;
    
    const updated = reorderApprovalLevels(index, newIndex);
    setSettings(updated);
  };

  const updateSetting = <K extends keyof ApprovalSettings>(
    key: K,
    value: ApprovalSettings[K]
  ) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const getConditionLabel = (level: ApprovalLevel) => {
    if (level.conditionType === 'always') {
      return 'Always required';
    }
    return `≥ ${new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(level.amountThreshold || 0)}`;
  };

  const getApproverLabel = (level: ApprovalLevel) => {
    if (level.approverType === 'role') {
      const role = ROLE_OPTIONS.find(r => r.value === level.approverRole);
      return role?.label || level.approverRole;
    }
    return 'Specific User';
  };

  return (
    <div className="space-y-6">
      {/* Approval Levels */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Approval Levels
          </CardTitle>
          <CardDescription>
            Configure multi-level approval workflow. Levels are processed in order (1 → 2 → 3...).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {settings.levels.length === 0 ? (
            <div className="text-center py-8 border-2 border-dashed rounded-lg">
              <AlertCircle className="h-12 w-12 text-text-muted mx-auto mb-3 opacity-50" />
              <p className="text-text-secondary">No approval levels configured</p>
              <p className="text-sm text-text-muted mt-1">
                Add approval levels to enable multi-level approval workflow
              </p>
              <Button onClick={() => setShowAddForm(true)} className="mt-4">
                <Plus className="mr-2 h-4 w-4" />
                Add First Level
              </Button>
            </div>
          ) : (
            <>
              {settings.levels.map((level, index) => (
                <div
                  key={level.id}
                  className="flex items-center gap-3 p-4 border border-border rounded-lg bg-background"
                >
                  {/* Drag Handle */}
                  <div className="flex flex-col gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={() => handleMoveLevel(index, 'up')}
                      disabled={index === 0}
                    >
                      <ArrowUp className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={() => handleMoveLevel(index, 'down')}
                      disabled={index === settings.levels.length - 1}
                    >
                      <ArrowDown className="h-3 w-3" />
                    </Button>
                  </div>

                  {/* Level Number */}
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-white font-bold">
                    {level.level}
                  </div>

                  {/* Level Details */}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-text-primary">{level.name}</p>
                    <div className="flex flex-wrap gap-2 mt-1">
                      <Badge variant="outline">
                        Approver: {getApproverLabel(level)}
                      </Badge>
                      <Badge variant="outline">
                        {level.departmentScope.includes('all') 
                          ? 'All Depts' 
                          : level.departmentScope.join(', ')}
                      </Badge>
                      <Badge variant={level.conditionType === 'always' ? 'default' : 'info'}>
                        {getConditionLabel(level)}
                      </Badge>
                    </div>
                  </div>

                  {/* Delete Button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => setLevelToDelete(level)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}

              <Button onClick={() => setShowAddForm(true)} variant="outline" className="w-full">
                <Plus className="mr-2 h-4 w-4" />
                Add Approval Level
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Add Level Form */}
      {showAddForm && (
        <Card className="border-primary">
          <CardHeader>
            <CardTitle>Add Approval Level</CardTitle>
            <CardDescription>
              Configure a new approval level (Level {settings.levels.length + 1})
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Level Name */}
            <div className="space-y-2">
              <Label>Level Name</Label>
              <Input
                placeholder="e.g., Manager Approval, Director Approval"
                value={newLevel.name}
                onChange={(e) => setNewLevel(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>

            {/* Approver Type */}
            <div className="space-y-2">
              <Label>Approver Type</Label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="approverType"
                    checked={newLevel.approverType === 'role'}
                    onChange={() => setNewLevel(prev => ({ ...prev, approverType: 'role' }))}
                  />
                  <span>By Role</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="approverType"
                    checked={newLevel.approverType === 'user'}
                    onChange={() => setNewLevel(prev => ({ ...prev, approverType: 'user' }))}
                  />
                  <span>By Specific User</span>
                </label>
              </div>
            </div>

            {/* Role or User Selection */}
            {newLevel.approverType === 'role' ? (
              <div className="space-y-2">
                <Label>Role</Label>
                <select
                  value={newLevel.approverRole}
                  onChange={(e) => setNewLevel(prev => ({ ...prev, approverRole: e.target.value }))}
                  className="h-10 px-3 py-2 border border-border rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary w-full"
                >
                  {ROLE_OPTIONS.map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>User ID</Label>
                <Input
                  placeholder="Enter user ID"
                  value={newLevel.approverUserId || ''}
                  onChange={(e) => setNewLevel(prev => ({ ...prev, approverUserId: e.target.value }))}
                />
              </div>
            )}

            {/* Department Scope */}
            <div className="space-y-2">
              <Label>Department Scope</Label>
              <div className="flex flex-wrap gap-2">
                {DEPARTMENT_OPTIONS.map((dept) => (
                  <label
                    key={dept.value}
                    className={`flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer transition-colors ${
                      newLevel.departmentScope.includes(dept.value as ApprovalDepartmentScope)
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={newLevel.departmentScope.includes(dept.value as ApprovalDepartmentScope)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setNewLevel(prev => ({ 
                            ...prev, 
                            departmentScope: [...prev.departmentScope, dept.value as ApprovalDepartmentScope]
                          }));
                        } else {
                          setNewLevel(prev => ({ 
                            ...prev, 
                            departmentScope: prev.departmentScope.filter(d => d !== dept.value)
                          }));
                        }
                      }}
                      className="sr-only"
                    />
                    <span className="text-sm">{dept.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Condition Type */}
            <div className="space-y-2">
              <Label>Condition</Label>
              <div className="flex flex-col gap-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="conditionType"
                    checked={newLevel.conditionType === 'always'}
                    onChange={() => setNewLevel(prev => ({ ...prev, conditionType: 'always' }))}
                  />
                  <span>Always required</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="conditionType"
                    checked={newLevel.conditionType === 'amount_threshold'}
                    onChange={() => setNewLevel(prev => ({ ...prev, conditionType: 'amount_threshold' }))}
                  />
                  <span>Only when amount exceeds threshold</span>
                </label>
              </div>
            </div>

            {/* Amount Threshold */}
            {newLevel.conditionType === 'amount_threshold' && (
              <div className="space-y-2">
                <Label>Amount Threshold (IDR)</Label>
                <Input
                  type="number"
                  min={0}
                  placeholder="e.g., 5000000"
                  value={newLevel.amountThreshold || ''}
                  onChange={(e) => setNewLevel(prev => ({ 
                    ...prev, 
                    amountThreshold: Number(e.target.value) 
                  }))}
                />
                <p className="text-xs text-text-muted">
                  This level is only required when total amount ≥ this value
                </p>
              </div>
            )}

            {/* Form Actions */}
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowAddForm(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddLevel}>
                <Plus className="mr-2 h-4 w-4" />
                Add Level
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Escalation Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            Escalation Settings
          </CardTitle>
          <CardDescription>
            Configure automatic escalation behavior
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 border border-border rounded-lg">
            <div className="space-y-1">
              <Label htmlFor="autoEscalation" className="text-base">
                Enable Auto-Escalation
              </Label>
              <p className="text-sm text-text-muted">
                Automatically escalate to next level after timeout
              </p>
            </div>
            <Switch
              id="autoEscalation"
              checked={settings.autoEscalationEnabled}
              onCheckedChange={(checked) => updateSetting('autoEscalationEnabled', checked)}
            />
          </div>

          {settings.autoEscalationEnabled && (
            <div className="space-y-2">
              <Label htmlFor="escalationHours">Escalation Timeout (Hours)</Label>
              <Input
                id="escalationHours"
                type="number"
                min={1}
                max={168}
                value={settings.escalationHours}
                onChange={(e) => updateSetting('escalationHours', Number(e.target.value))}
                className="w-full md:w-48"
              />
              <p className="text-xs text-text-muted">
                Hours to wait before auto-escalating to next approval level
              </p>
            </div>
          )}

          <div className="flex items-center justify-between p-4 border border-border rounded-lg">
            <div className="space-y-1">
              <Label htmlFor="allowDelegation" className="text-base">
                Allow Delegation
              </Label>
              <p className="text-sm text-text-muted">
                Allow approvers to delegate to another user
              </p>
            </div>
            <Switch
              id="allowDelegation"
              checked={settings.allowDelegation}
              onCheckedChange={(checked) => updateSetting('allowDelegation', checked)}
            />
          </div>

          <div className="flex items-center justify-between p-4 border border-border rounded-lg">
            <div className="space-y-1">
              <Label htmlFor="notifyDelegation" className="text-base">
                Notify on Delegation
              </Label>
              <p className="text-sm text-text-muted">
                Send notification when approval is delegated
              </p>
            </div>
            <Switch
              id="notifyDelegation"
              checked={settings.notifyOnDelegation}
              onCheckedChange={(checked) => updateSetting('notifyOnDelegation', checked)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={handleReset}>
          <RotateCcw className="mr-2 h-4 w-4" />
          Reset to Defaults
        </Button>
        <Button onClick={handleSave}>
          <Save className="mr-2 h-4 w-4" />
          {saved ? 'Saved!' : 'Save Changes'}
        </Button>
      </div>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={!!levelToDelete}
        onOpenChange={(open) => !open && setLevelToDelete(null)}
        title="Delete Approval Level"
        description={`Are you sure you want to delete "${levelToDelete?.name}"? This will also update the level numbers of subsequent levels.`}
        onConfirm={() => levelToDelete && handleDeleteLevel(levelToDelete)}
        confirmLabel="Delete"
        isDeleting={false}
      />

      {/* Info */}
      <Card className="bg-muted/50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <Info className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <div className="text-sm text-text-secondary">
              <p className="font-medium text-text-primary mb-1">Multi-Level Approval</p>
              <p>
                Approval levels are processed in order. Each level must approve before moving to the next.
                You can set conditions (e.g., only require Director approval for amounts ≥ 10M).
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
